'use client';

import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { BehaviorSubject, Subject } from 'rxjs';

import type { ChatStorageManager } from '../../functions/storage/ChatStorageManager';
import { DefaultChatStorageManager } from '../../functions/storage/DefaultChatStorageManager';
import { useChatPendingMessagesManager } from '../../hooks/useChatPendingMessagesManager';
import type { ChatEvent } from '../../types/ChatEvent';
import { ChatGatewayProvider } from '../gateway/provider';
import { ChatSessionProvider } from '../session/provider';
import {
  type ActiveChat,
  GlobalChatContext,
  type GlobalChatContextValue,
  type ReceivedChatNotificationSummary,
} from './context';

interface Props {
  children?: ReactNode;
}

/**
 * Top-level chat provider. Owns the long-lived subjects (events,
 * active chat, unread counts, notification summary, pending
 * messages), then nests the gateway + session providers so the
 * websocket connection and sessions list state both share this scope.
 *
 * Settings/profile wiring is deferred — `settings: undefined`.
 */
export function ChatGlobalProvider({ children }: Props) {
  // Reactive view of the current user id, sourced from the
  // surrounding <AuthProvider>. `undefined` while the auth SDK is
  // still restoring the session (first paint), `null` once we know
  // the user is signed out.
  const currentUserId = useCurrentUserId();

  const [storage, setStorage] = useState<ChatStorageManager>(
    () => new DefaultChatStorageManager(currentUserId),
  );
  useEffect(() => {
    setStorage(new DefaultChatStorageManager(currentUserId));
  }, [currentUserId]);

  const [activeChat$] = useState(
    () => new BehaviorSubject<ActiveChat | undefined>(undefined),
  );

  const [events$, setEvents$] = useState(() => new Subject<ChatEvent>());
  useEffect(() => {
    setEvents$(new Subject<ChatEvent>());
  }, [currentUserId]);

  const [receivedChatNotificationSummary$] = useState(
    () =>
      new BehaviorSubject<ReceivedChatNotificationSummary>({
        senders: new Map(),
        messageCount: 0,
      }),
  );

  const { pendingMessages$ } = useChatPendingMessagesManager(
    events$,
    storage,
    currentUserId,
  );

  const value = useMemo<GlobalChatContextValue>(
    () => ({
      storage,
      activeChat$,
      events$,
      pendingMessages$,
      receivedChatNotificationSummary$,
      settings: undefined,
    }),
    [
      storage,
      activeChat$,
      events$,
      pendingMessages$,
      receivedChatNotificationSummary$,
    ],
  );

  return (
    <GlobalChatContext.Provider value={value}>
      <ChatGatewayProvider>
        <ChatSessionProvider>{children}</ChatSessionProvider>
      </ChatGatewayProvider>
    </GlobalChatContext.Provider>
  );
}
